import { useMediaQuery, useTheme } from '@mui/material';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  APP_SETUP_PAGE,
  ContractSettings,
  ModuleSettingsItem,
} from 'src/constants';
import { DEFAULT_CONTRACT_DATE_FORMAT } from 'src/constants/dateConsts';
import { AppSettingsModalContext } from 'src/features/app-settings-modal/AppSettingsModalContext';
import history from 'src/history';
import { useContractSettingsForm } from 'src/hooks/useContractSettingsForm';
import { SetupModulePageDescription } from 'src/legacy/components/Settings/AppSetupPage/SetupApp';
import SetupAppBreadCrumbs from 'src/legacy/components/Settings/AppSetupPage/SetupApp/SetupAppBreadCrumbs';
import { useEditingApp } from 'src/legacy/components/Settings/AppSetupPage/SetupApp/useEditingApp';
import { useAppSetupPaths } from 'src/legacy/components/Settings/AppSetupPage/useAppSetupPaths';
import { ModuleContainer } from 'src/legacy/components/Settings/ModuleContainer';
import { SettingsPageForm } from 'src/legacy/components/Settings/SettingsPageForm';
import { useGetInstallsQuery } from 'src/services/api/applicationsApi';
import { RootState } from 'src/store';
import { updateContractSettings } from 'src/store/settings/actions';

const ContractSettingsPage = () => {
  const { folderId, addAppPagePath } = useAppSetupPaths();
  const dispatch = useDispatch();
  const modalCtx = React.useContext(AppSettingsModalContext);
  const contractModule = useEditingApp();
  const contractSettings = useSelector(
    (state: RootState) => state.settings.contractSettings,
  );
  const { data: { moduleSettings } = {} } = useGetInstallsQuery();

  const initialValues = React.useMemo(
    () => ({
      id: contractModule?.id || '',
      // Module can be hardcoded for core apps.
      type: 'module' as const,
      disabled: Boolean(contractModule?.disabled),
      label: contractModule?.label || '',
      icon: contractModule?.icon || '',
      isEmailAttachmentExcluded: Boolean(
        contractSettings?.isEmailAttachmentExcluded,
      ),
      dateFormat: contractSettings?.dateFormat || DEFAULT_CONTRACT_DATE_FORMAT,
      iuSidebarHidden:
        moduleSettings?.find((m) => m.id === contractModule?.id)
          ?.iuSidebarHidden ?? false,
    }),
    [contractSettings, contractModule, moduleSettings],
  );

  const handleSave = async (values: ContractSettings & ModuleSettingsItem) => {
    dispatch(
      updateContractSettings({
        contractSettings: values,
        currentFileModule: contractModule,
        folderId,
      }),
    );

    if (!modalCtx) {
      history.push(APP_SETUP_PAGE.path);
    }
  };

  const handleCancel = () => {
    if (contractModule?.disabled) {
      if (modalCtx) {
        modalCtx.onClose();
      } else {
        history.push(addAppPagePath);
      }
    }
  };

  const theme = useTheme();
  const isSm = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <ModuleContainer description={<SetupModulePageDescription />}>
      <SettingsPageForm
        BreadcrumbsComponent={<SetupAppBreadCrumbs />}
        useFormHook={useContractSettingsForm}
        initialFormValue={initialValues}
        handleSave={handleSave}
        actionLabel={contractModule?.disabled ? 'Enable' : 'Save'}
        shouldShowPageAction={contractModule?.disabled}
        handleCancel={handleCancel}
        showBackButton={isSm}
        onBackButtonClick={modalCtx?.onClose ?? (() => history.goBack())}
      />
    </ModuleContainer>
  );
};

export default ContractSettingsPage;
